import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Text,
  VStack,
  Badge,
  Image,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { ArrowBackIcon, EditIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getDataByIdSuperAdmin } from "../../redux/actions/common.action";

const ViewNavbarContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch: any = useDispatch();

  const { navbarContent, loading } = useSelector((state: any) => state.common);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (id) {
      setDataLoaded(false);
      dispatch(getDataByIdSuperAdmin(id, "navbar-content") as any);
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (navbarContent?.Data) {
      setDataLoaded(true);
    }
  }, [navbarContent]);

  if (loading || !dataLoaded) {
    return (
      <Box p="8px" bg="gray.50" minH="100vh">
        <Flex justify="center" align="center" minH="60vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.600" fontSize="sm">
              Loading content...
            </Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  if (!navbarContent?.Data) {
    return (
      <Box p="8px" bg="gray.50" minH="100vh">
        <Box textAlign="center" py={20}>
          <Text fontSize="lg" color="gray.600">
            Content not found
          </Text>
          <Button
            mt={4}
            size="sm"
            onClick={() => navigate("/navbar-content-list")}
          >
            Go Back
          </Button>
        </Box>
      </Box>
    );
  }

  const content = navbarContent.Data;

  return (
    <Box bg="gray.50" minH="100vh" py={4}>
      {/* Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        p="12px"
        mx="8px"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <IconButton
              aria-label="Go back"
              icon={<ArrowBackIcon />}
              onClick={() => navigate("/navbar-content-list")}
              variant="ghost"
              size="sm"
              _hover={{ bg: "gray.100" }}
            />
          </HStack>
          <HStack spacing={2}>
            <Badge
              colorScheme="purple"
              fontSize="xs"
              px={3}
              py={1}
              borderRadius="full"
              fontWeight="medium"
            >
              Preview Mode
            </Badge>
            <Button
              leftIcon={<EditIcon />}
              size="sm"
              fontSize="xs"
              colorScheme="blue"
              onClick={() => navigate(`/navbar-content/edit/${id}`)}
            >
              Edit
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Content Container - Same as Public Website */}
      <Box maxW="1200px" mx="auto" px={6}>
        {/* Breadcrumb */}
        <Flex
          align="center"
          gap={2}
          fontSize="0.875rem"
          color="gray.600"
          mb={6}
        >
          <Text>{content.section}</Text>
          <Text color="gray.400">›</Text>
          <Text>{content.subSection}</Text>
          <Text color="gray.400">›</Text>
          <Text color="blue.600" fontWeight="600">
            {content.title}
          </Text>
        </Flex>

        {/* Page Header */}
        <Box textAlign="center" mb={10}>
          <Text
            fontSize={{ base: "1.75rem", md: "2.5rem" }}
            fontWeight="800"
            color="gray.900"
            mb={2}
          >
            {content.title}
          </Text>
          <Text
            fontSize={{ base: "0.95rem", md: "1.1rem" }}
            color="gray.600"
            fontWeight="500"
          >
            {content.subSection}
          </Text>
        </Box>

        {/* Page Sections */}
        <VStack spacing={10} align="stretch">
          {content.pageSections?.map((section: any, index: number) => {
            const hasImage = section.image;
            const isImageLeft = index % 2 === 0;

            return (
              <Box
                key={index}
                display="grid"
                gridTemplateColumns={
                  hasImage
                    ? isImageLeft
                      ? { base: "1fr", md: "400px 1fr" }
                      : { base: "1fr", md: "1fr 400px" }
                    : "1fr"
                }
                gap={8}
                alignItems="start"
              >
                {hasImage && (
                  <Box
                    borderRadius="16px"
                    overflow="hidden"
                    boxShadow="0 8px 24px rgba(0, 0, 0, 0.08)"
                    order={!hasImage || isImageLeft ? 0 : { base: 0, md: 2 }}
                    position={{ base: "relative", md: "sticky" }}
                    top={{ base: "0", md: "100px" }}
                    height="fit-content"
                    transition="all 0.4s ease"
                    _hover={{
                      transform: { md: "translateY(-4px)" },
                      boxShadow: { md: "0 12px 32px rgba(0, 0, 0, 0.12)" },
                    }}
                  >
                    <Image
                      src={section.image}
                      alt={section.sectionTitle}
                      w="full"
                      h="auto"
                      display="block"
                      transition="transform 0.4s ease"
                      _hover={{
                        transform: { md: "scale(1.05)" },
                      }}
                    />
                  </Box>
                )}

                <Box
                  bg="white"
                  borderRadius="16px"
                  p={8}
                  boxShadow="0 4px 16px rgba(0, 0, 0, 0.06)"
                  border="1px"
                  borderColor="gray.100"
                  order={hasImage && !isImageLeft ? { base: 0, md: 1 } : 0}
                  transition="all 0.4s ease"
                  _hover={{
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
                    borderColor: "gray.200",
                  }}
                >
                  <Text
                    fontSize={{ base: "1.25rem", md: "1.75rem" }}
                    fontWeight="700"
                    color="gray.900"
                    mb={5}
                    position="relative"
                    pb={3}
                    _after={{
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "50px",
                      height: "3px",
                      bg: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                      borderRadius: "2px",
                    }}
                  >
                    {section.sectionTitle}
                  </Text>

                  <Box
                    fontSize="1rem"
                    lineHeight="1.7"
                    color="gray.700"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                    sx={{
                      "& p": {
                        mb: 4,
                      },
                      "& p:last-child": {
                        mb: 0,
                      },
                      "& ul, & ol": {
                        margin: "16px 0 16px 24px",
                      },
                      "& li": {
                        mb: 2,
                        pl: 1,
                      },
                      "& a": {
                        color: "blue.600",
                        textDecoration: "none",
                        fontWeight: "500",
                        borderBottom: "1px solid transparent",
                        transition: "all 0.3s",
                        _hover: {
                          color: "blue.700",
                          borderBottomColor: "blue.700",
                        },
                      },
                      "& strong": {
                        color: "gray.900",
                        fontWeight: "600",
                      },
                      "& h3, & h4": {
                        color: "gray.900",
                        fontWeight: "600",
                        mt: 5,
                        mb: 3,
                      },
                      "& h3": {
                        fontSize: "1.25rem",
                      },
                      "& h4": {
                        fontSize: "1.1rem",
                      },
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </VStack>

        {/* Sub Features Section */}
        {content.showSubFeature && content.subFeature?.name && (
          <Box mt={16}>
            <Text
              fontSize={{ base: "1.75rem", md: "2.25rem" }}
              fontWeight="800"
              color="gray.900"
              mb={8}
              textAlign="center"
            >
              {content.subFeature.name}
            </Text>

            <Box
              bg="white"
              borderRadius="20px"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.08)"
              border="1px"
              borderColor="gray.100"
              p={{ base: 6, md: 10 }}
              display="grid"
              gridTemplateColumns={{ base: "1fr", md: "350px 1fr" }}
              gap={10}
              alignItems="center"
            >
              {/* Image - Left Side */}
              {content.subFeature.image && (
                <Box
                  borderRadius="16px"
                  overflow="hidden"
                  boxShadow="0 4px 20px rgba(0, 0, 0, 0.1)"
                  position="relative"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bg: "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))",
                    zIndex: 1,
                    opacity: 0,
                    transition: "opacity 0.3s",
                  }}
                  _hover={{
                    _before: {
                      opacity: 1,
                    },
                  }}
                >
                  <Image
                    src={content.subFeature.image}
                    alt={content.subFeature.name}
                    w="full"
                    h="auto"
                    objectFit="contain"
                    display="block"
                  />
                </Box>
              )}

              {/* Features - Right Side in Grid */}
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} w="full">
                {content.subFeature.featured
                  ?.filter((item: string) => item.trim())
                  .map((feature: string, idx: number) => (
                    <Flex
                      key={idx}
                      align="flex-start"
                      gap={3}
                      p={3}
                      borderRadius="12px"
                      transition="all 0.3s"
                      _hover={{
                        bg: "blue.50",
                        transform: "translateX(4px)",
                      }}
                    >
                      <CheckCircleIcon
                        color="green.500"
                        boxSize={5}
                        mt={0.5}
                        flexShrink={0}
                      />
                      <Text
                        fontSize="0.95rem"
                        color="gray.700"
                        fontWeight="500"
                        lineHeight="1.6"
                      >
                        {feature}
                      </Text>
                    </Flex>
                  ))}
              </SimpleGrid>
            </Box>
          </Box>
        )}

        {/* Benefits Section */}
        {content.showBenefits && content.benefits?.sections?.length > 0 && (
          <Box mt={16}>
            {content.benefits.name && (
              <Text
                fontSize={{ base: "1.75rem", md: "2.25rem" }}
                fontWeight="800"
                color="gray.900"
                mb={8}
                textAlign="center"
              >
                {content.benefits.name}
              </Text>
            )}

            <VStack spacing={6} align="stretch">
              {content.benefits.sections.map((benefit: any, idx: number) => (
                <Box
                  key={idx}
                  bg="white"
                  borderRadius="16px"
                  boxShadow="0 4px 20px rgba(0, 0, 0, 0.06)"
                  border="1px"
                  borderColor="gray.100"
                  overflow="hidden"
                  transition="all 0.4s ease"
                  _hover={{
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                    transform: "translateY(-4px)",
                    borderColor: "blue.200",
                  }}
                >
                  <Flex
                    direction={{ base: "column", md: "row" }}
                    align="stretch"
                    gap={0}
                  >
                    {/* Image - Full Height on Left */}
                    {benefit.image && (
                      <Box
                        w={{ base: "100%", md: "280px" }}
                        h={{ base: "200px", md: "auto" }}
                        flexShrink={0}
                        position="relative"
                        overflow="hidden"
                      >
                        <Image
                          src={benefit.image}
                          alt={benefit.title}
                          w="full"
                          h="full"
                          objectFit="contain"
                          objectPosition="center"
                          display="block"
                          bg="gray.50"
                          p={4}
                        />
                      </Box>
                    )}

                    {/* Content */}
                    <Box flex="1" p={{ base: 5, md: 7 }}>
                      <Text
                        fontSize={{ base: "1.1rem", md: "1.35rem" }}
                        fontWeight="700"
                        color="gray.900"
                        mb={3}
                      >
                        {benefit.title}
                      </Text>
                      <Text
                        fontSize="0.95rem"
                        color="gray.600"
                        lineHeight="1.7"
                      >
                        {benefit.description}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {/* Industries Section */}
        {content.showIndustries &&
          content.industriesData?.industries?.length > 0 && (
            <Box mt={16}>
              {content.industriesData.heading && (
                <Text
                  fontSize={{ base: "1.75rem", md: "2.25rem" }}
                  fontWeight="800"
                  color="gray.900"
                  mb={8}
                  textAlign="center"
                >
                  {content.industriesData.heading}
                </Text>
              )}

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {content.industriesData.industries.map(
                  (industry: any, idx: number) => (
                    <Box
                      key={idx}
                      bg="white"
                      borderRadius="16px"
                      boxShadow="0 4px 16px rgba(0, 0, 0, 0.06)"
                      border="1px"
                      borderColor="gray.100"
                      overflow="hidden"
                      transition="all 0.4s ease"
                      _hover={{
                        boxShadow: "0 8px 28px rgba(0, 0, 0, 0.12)",
                        transform: "translateY(-6px)",
                        borderColor: "blue.200",
                      }}
                    >
                      {/* Image Container - Full Image Visible */}
                      {industry.image && (
                        <Box
                          w="full"
                          h="180px"
                          bg="gray.50"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          p={4}
                          overflow="hidden"
                        >
                          <Image
                            src={industry.image}
                            alt={industry.title}
                            maxW="full"
                            maxH="full"
                            objectFit="contain"
                            display="block"
                          />
                        </Box>
                      )}

                      {/* Content */}
                      <Box p={6}>
                        <Text
                          fontSize="1.15rem"
                          fontWeight="700"
                          color="gray.900"
                          mb={3}
                        >
                          {industry.title}
                        </Text>
                        <Text
                          fontSize="0.9rem"
                          color="gray.600"
                          lineHeight="1.6"
                        >
                          {industry.description}
                        </Text>
                      </Box>
                    </Box>
                  ),
                )}
              </SimpleGrid>
            </Box>
          )}

        {/* FAQs Section - Accordion Style */}
        {content.showFAQs && content.faqs?.length > 0 && (
          <Box mt={16} mb={10}>
            <Text
              fontSize={{ base: "1.75rem", md: "2.25rem" }}
              fontWeight="800"
              color="gray.900"
              mb={8}
              textAlign="center"
            >
              Frequently Asked Questions
            </Text>

            <Box
              maxW="900px"
              mx="auto"
              bg="white"
              borderRadius="16px"
              boxShadow="0 4px 20px rgba(0, 0, 0, 0.06)"
              border="1px"
              borderColor="gray.100"
              overflow="hidden"
            >
              <Accordion allowMultiple>
                {content.faqs.map((faq: any, idx: number) => (
                  <AccordionItem
                    key={idx}
                    border="none"
                    borderBottom={
                      idx < content.faqs.length - 1 ? "1px" : "none"
                    }
                    borderColor="gray.100"
                  >
                    {({ isExpanded }) => (
                      <>
                        <AccordionButton
                          py={5}
                          px={6}
                          _hover={{
                            bg: "blue.50",
                          }}
                          transition="all 0.3s"
                        >
                          <Box
                            flex="1"
                            textAlign="left"
                            fontSize="1.05rem"
                            fontWeight="600"
                            color={isExpanded ? "blue.600" : "gray.900"}
                            transition="color 0.3s"
                          >
                            {faq.question}
                          </Box>
                          <AccordionIcon
                            fontSize="1.5rem"
                            color={isExpanded ? "blue.600" : "gray.500"}
                            transition="all 0.3s"
                          />
                        </AccordionButton>
                        <AccordionPanel
                          pb={5}
                          px={6}
                          pt={0}
                          fontSize="0.95rem"
                          color="gray.600"
                          lineHeight="1.7"
                          bg="gray.50"
                        >
                          {faq.answer}
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                ))}
              </Accordion>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ViewNavbarContent;
